import { LandingTranslationType } from "../../features/landing.types";

export const landing: LandingTranslationType = {
  landing: {
    hero: {
      title: "Craft Your Tale with AI – Where Your Decisions Shape the Journey",
      subtitle: "Inspired by the classic choose-your-own-adventure stories",
      description: "Use our AI-assisted interactive storytelling platform to create engaging stories and characters",
      startStory: "Start Your Story",
      signIn: "Sign In"
    },
    features: {
      title: "Unleash Your Creativity",
      aiPowered: {
        title: "AI-Powered Stories",
        description: "Create unique narratives with our advanced AI storytelling engine"
      },
      interactiveChoices: {
        title: "Interactive Choices",
        description: "Shape your story through meaningful decisions and custom actions"
      },
      creativeFreedom: {
        title: "Creative Freedom",
        description: "Write your own path with unlimited creative possibilities"
      }
    },
    preview: {
      title: "Preview a Tale",
      storyText: "The ancient door creaked open, revealing a chamber bathed in ethereal blue light. Crystal formations jutted from the walls, their surfaces reflecting countless tiny rainbows across the room. In the center, hovering inches above a pedestal of black marble, was a book bound in what appeared to be starlight itself...",
      whatToDo: "What do you do?",
      choice1: "Approach the book cautiously, studying the room for traps",
      choice2: "Use your magic to sense any dangerous enchantments",
      choice3: "Boldly grab the book - fortune favors the brave!"
    },
    callToAction: {
      title: "Ready to Begin Your Journey?",
      subtitle: "Join thousands of storytellers creating unique adventures with AI",
      button: "Start Writing Now"
    },
    storyPreview: {
      header: "Experience Immersive Storytelling",
      description: "Create, explore and shape stories in a dynamic interactive environment with {{appName}}",
      genres: "Genres",
      themes: "Themes",
      environment: "Environment",
      writeResponse: "Write your own response...",
      previousStory: "Previous story",
      nextStory: "Next story",
      goToStory: "Go to story {{number}}"
    },
    creativeSuite: {
      title: "Creative Suite",
      description: "Powerful tools to unlock your storytelling potential and bring your imagination to life",
      categories: {
        stories: "Stories",
        characters: "Characters",
        premium: "Premium"
      },
      premiumFeature: "Premium Feature",
      basicFeature: "Available in Basic Plan",
      freeFeature: "Free for Everyone",
      getStarted: "Get Started",
      noCreditCard: "No credit card required to start your creative journey",
      tools: {
        aiWriting: {
          title: "AI-Powered Writing",
          description: "Create choose-your-own-adventure stories with advanced AI. Advance with suggestion responses or write your own."
        },
        genresThemes: {
          title: "Extensive Genres & Themes & Environments",
          description: "Access a limited set of genres as a free user, or unlock all genres and themes with Basic."
        },
        sequels: {
          title: "Story Sequels & Series",
          description: "Continue your best stories with sequels and build engaging story series."
        },
        characterBuilder: {
          title: "Character Builder",
          description: "Design complex characters with detailed personalities, motivations, and backstories."
        },
        aiPortraits: {
          title: "AI Character Portraits",
          description: "Generate unique visual representations of your characters with our AI portrait generator."
        },
        characterExtract: {
          title: "Character Extraction",
          description: "Extract characters from your stories automatically to build your character library."
        },
        sharing: {
          title: "Story Sharing & Publishing",
          description: "Share your stories and make them public for others to enjoy and appreciate."
        },
        unlimitedStorage: {
          title: "Increased Story Limit",
          description: "Create up to 5 stories per day with Free, 25 with Basic, or unlimited stories with Premium."
        },
        statsBadges: {
          title: "Statistics & Badges",
          description: "Track your writing stats and earn badges for your storytelling achievements."
        }
      }
    },
    exampleStories: {
      dragonsRiddle: {
        title: "The Dragon's Riddle",
        excerpt: "The ancient dragon posed a riddle that had remained unsolved for centuries. \"Answer correctly,\" it growled, \"and I shall grant you passage through the mountain.\" The massive creature's scales gleamed like obsidian in the dim cavern light. I stood there, fingertips trembling against cold stone, my companions frozen behind me. The dragon's amber eyes fixed on mine. \"But fail,\" the beast continued, \"and you shall join the bones of those who came before.\" It gestured to scattered remains I'd tried not to notice. Dragons were fond of wordplay and hidden meanings. The fate of our quest hinged on my ability to decipher the creature's puzzle.",
        suggestion1: "\"The answer is 'time'—it devours all, yet itself is never consumed,\" I proclaimed with more confidence than I felt.",
        suggestion2: "I respectfully bowed to the ancient beast. \"Great one, might I ask a clarifying question about your riddle?\"",
        suggestion3: "I glanced at my companions' fearful faces before turning back to the dragon. \"Perhaps we could negotiate another way through?\""
      },
      echoesFromTomorrow: {
        title: "Echoes from Tomorrow",
        excerpt: "Dr. Miranda Chen stared at the message on her screen. It had arrived yesterday, but the timestamp showed it was sent tomorrow. She read it again: \"Protocol Omega initiated. Do NOT proceed with test sequence. Catastrophic temporal collapse imminent.\" Miranda checked the quantum communication array she'd designed—it was meant to reduce signal latency, not pierce the veil between now and tomorrow. The quantum signatures embedded in the message matched their encryption protocols exactly, with one impossible anomaly: they showed decay patterns that couldn't exist yet. As her terminal chimed with another incoming message, Miranda felt a chill. The sender ID displayed her own name.",
        suggestion1: "Miranda typed quickly: \"This is Dr. Chen. Identify yourself and explain how you're accessing our secure systems.\"",
        suggestion2: "She initiated an emergency shutdown while backing up all project data to the secure offsite server.",
        suggestion3: "She called her research partner: \"Kenji, something impossible is happening with the quantum array.\""
      },
      forgottenLibrary: {
        title: "The Forgotten Library",
        excerpt: "The library appeared only on rainy nights, its doors opening to those who needed answers most. I had been searching for it for years, following rumors and obscure references. Now, as I stood before its impossible architecture—larger on the inside than outside—I hesitated. The massive oak doors stood slightly ajar, warm light spilling onto wet cobblestones. The entrance hall rose impossibly high, shelves stretching beyond where the ceiling should be. The air smelled of paper, ink, and something older—like time itself had a scent. I noticed whispers growing stronger, coming from thousands of books all speaking at once. Then, one voice rose above the others: \"We've been waiting for you.\"",
        suggestion1: "\"Who's there?\" I called out, my voice echoing through the cavernous space as I scanned the shadowy aisles.",
        suggestion2: "I approached the nearest shelf and ran my fingers along the ancient spines, wondering which book had spoken.",
        suggestion3: "\"I've come seeking information about the Celestial Convergence,\" I announced to the whispering library."
      }
    }
  }
};
